<script setup lang="ts">
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';
import { X } from 'lucide-vue-next';
import { nextTick, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useBodyScrollLock } from '@/composables/useBodyScrollLock';

const props = withDefaults(defineProps<{
  isOpen: boolean;
  title: string;
  closable?: boolean;
}>(), {
  closable: true,
});

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const { t } = useI18n();

const modalRef = ref<HTMLElement | null>(null);

const { lock: lockBodyScroll, unlock: unlockBodyScroll } = useBodyScrollLock();

const { activate, deactivate } = useFocusTrap(modalRef, {
  escapeDeactivates: false,
  returnFocusOnDeactivate: true,
  allowOutsideClick: true,
  immediate: false,
  fallbackFocus: () => modalRef.value!,
});

function closeModal() {
  emit('close');
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape' && props.closable) {
    closeModal();
  }
}

watch(() => props.isOpen, async (newValue) => {
  if (newValue) {
    lockBodyScroll();
    document.addEventListener('keydown', handleKeydown);
    await nextTick();
    activate();
  }
  else {
    document.removeEventListener('keydown', handleKeydown);
    deactivate();
    setTimeout(() => {
      if (!props.isOpen) {
        unlockBodyScroll();
      }
    }, 300);
  }
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown);
  deactivate();
});
</script>

<template>
  <Transition name="mobile-modal-transition">
    <div
      v-if="isOpen"
      ref="modalRef"
      tabindex="-1"
      class="mobile-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="mobile-modal-title"
      @click.self="closable && closeModal()"
    >
      <div class="modal-content">
        <div class="modal-header">
          <h3 id="mobile-modal-title" class="modal-title">
            <slot name="title">
              {{ title }}
            </slot>
          </h3>
          <button
            v-if="closable"
            class="close-button"
            :aria-label="t('common.close')"
            @click="closeModal"
          >
            <X :size="24" aria-hidden="true" />
          </button>
        </div>
        <div class="modal-body">
          <slot name="body" />
        </div>
        <div v-if="$slots.footer" class="modal-footer">
          <slot name="footer" />
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped lang="css">
.mobile-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  z-index: 9999;
  display: flex;
  align-items: flex-end;
  overscroll-behavior: contain;
}

.modal-content {
  background-color: var(--yb-color-white);
  border-radius: var(--yb-radius-base) var(--yb-radius-base) 0 0;
  width: 100%;
  /* vh is the viewport without Safari's toolbars on iOS: the header would overflow above the screen */
  max-height: 90vh;
  max-height: 90dvh;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  padding: var(--yb-spacing-base);
  border-bottom: 1px solid var(--yb-color-lightgray);
}

.modal-title {
  font-size: var(--yb-text-xl);
  font-weight: var(--yb-text-bold);
  color: var(--yb-color-black);
  margin: 0;
}

.close-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--yb-color-black);
}

.modal-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: var(--yb-spacing-base);
  display: flex;
  flex-direction: column;
  gap: var(--yb-spacing-base);
}

.modal-footer {
  flex-shrink: 0;
  padding: var(--yb-spacing-base);
  border-top: 1px solid var(--yb-color-lightgray);
}

.mobile-modal-transition-enter-active,
.mobile-modal-transition-leave-active {
  transition: background-color 0.5s ease-out;
}

.mobile-modal-transition-enter-from,
.mobile-modal-transition-leave-to {
  background-color: rgba(0, 0, 0, 0);
}

.mobile-modal-transition-enter-active .modal-content,
.mobile-modal-transition-leave-active .modal-content {
  transition: transform 0.5s ease-out;
}

.mobile-modal-transition-enter-from .modal-content,
.mobile-modal-transition-leave-to .modal-content {
  transform: translateY(100%);
}

@media (prefers-reduced-motion: reduce) {
  .mobile-modal-transition-enter-active,
  .mobile-modal-transition-leave-active,
  .mobile-modal-transition-enter-active .modal-content,
  .mobile-modal-transition-leave-active .modal-content {
    transition-duration: 0.01ms !important;
  }
}
</style>
